/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import { View, Text, StyleSheet, Platform, Animated } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../../theme";

// Cross-platform import for VictoryPie and VictoryContainer
let VictoryPie, VictoryContainer;
if (Platform.OS === "web") {
  // @ts-ignore
  ({ VictoryPie, VictoryContainer } = require("victory"));
} else {
  // @ts-ignore
  ({ VictoryPie, VictoryContainer } = require("victory-native"));
}

interface PreferredMethodChecklistProps {
  width?: string | number;
  dueDate?: string;
  status?: "pending" | "completed" | "overdue";
  methods?: string[];
  selectedMethods?: string[];
  onSelectMethod?: (method: string) => void;
  yesCount?: number;
  noCount?: number;
  naCount?: number;
}

const PreferredMethodChecklist: React.FC<PreferredMethodChecklistProps> = ({
  width = "100%",
  dueDate = "Not scheduled",
  status = "pending",
  methods = [],
  selectedMethods = [],
  onSelectMethod = () => {},
  yesCount = 0,
  noCount = 0,
  naCount = 0,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const totalCount = yesCount + noCount + naCount;

  // Create animated values for checkbox animations
  const animatedValues = React.useMemo(
    () => methods.map(() => new Animated.Value(0)),
    [methods]
  );

  // Function to handle method selection with animation
  const handleMethodSelect = (method: string, index: number) => {
    const isSelected = selectedMethods.includes(method);

    // Animate the checkbox with a spring animation for better feedback
    Animated.spring(animatedValues[index], {
      toValue: isSelected ? 0 : 1,
      useNativeDriver: true,
      damping: 12,
      stiffness: 120,
    }).start();

    // Call the provided onSelectMethod callback
    onSelectMethod(method);
  };

  const getStatusIcon = () => {
    switch (status) {
      case "completed":
        return (
          <MaterialIcons
            name="check-circle"
            size={24}
            color="#4CAF50"
            testID="status-icon"
          />
        );
      case "overdue":
        return (
          <MaterialIcons
            name="warning"
            size={24}
            color="#FF5722"
            testID="status-icon"
          />
        );
      case "pending":
      default:
        return (
          <MaterialIcons
            name="schedule"
            size={24}
            color="#FFC107"
            testID="status-icon"
          />
        );
    }
  };

  const getStatusText = () => {
    switch (status) {
      case "completed":
        return "Completed";
      case "overdue":
        return "Overdue";
      case "pending":
      default:
        return "Pending";
    }
  };

  return (
    <View
      style={[
        styles.container,
        { width } as any,
        isDarkTheme && {
          backgroundColor: colors.background.surface,
          borderWidth: 1,
          borderColor: colors.border.default,
        },
      ]}
      testID="preferred-method-container"
    >
      <View style={styles.header} testID="header">
        <MaterialIcons
          name="checklist"
          size={20}
          color="#fff"
          style={styles.headerIcon}
        />
        <Text style={styles.headerText}>Preferred Method Checklist</Text>
      </View>

      <View style={styles.content}>
        <View style={styles.graphContainer} testID="graph-container">
          <View
            style={[
              styles.chartWrapper,
              {
                backgroundColor: "transparent",
                elevation: 0,
                shadowColor: "transparent",
              },
            ]}
            testID="chart-wrapper"
          >
            <VictoryPie
              data={[
                { x: "Yes", y: yesCount || 0 },
                { x: "No", y: noCount || 0 },
                { x: "N/A", y: naCount || 0 },
              ]}
              colorScale={["#33ABDA", "#B05788", "#0078A7"]}
              width={180}
              height={180}
              padding={30}
              innerRadius={80}
              cornerRadius={0}
              startAngle={-6}
              labels={() => null}
              animate={{
                duration: 800,
                easing: "bounce",
                onLoad: { duration: 500 },
              }}
              style={{
                data: {
                  fillOpacity: 0.95,
                  stroke: "#fff",
                  strokeWidth: 2,
                },
              }}
              containerComponent={
                <VictoryContainer
                  responsive={false}
                  style={{
                    touchAction: "auto",
                    userSelect: "auto",
                  }}
                />
              }
            />
            {/* <View style={styles.totalCount}>
              <Text style={styles.totalCountText}>Total</Text>
              <Text style={styles.totalCountNumber}>
                {yesCount + noCount + naCount}
              </Text>
            </View> */}
          </View>
          <View style={styles.legendContainer}>
            <View style={styles.legendItem} testID="legend-item">
              <View
                style={[styles.legendColor, { backgroundColor: "#33ABDA" }]}
              />
              <Text style={styles.legendText}>
                Yes: {yesCount} (
                {Math.round((yesCount / totalCount) * 100) || 0}
                %)
              </Text>
            </View>
            <View style={styles.legendItem} testID="legend-item">
              <View
                style={[styles.legendColor, { backgroundColor: "#B05788" }]}
              />
              <Text style={styles.legendText}>
                No: {noCount} (
                {Math.round((noCount / totalCount) * 100) || 0}
                %)
              </Text>
            </View>
            <View style={styles.legendItem} testID="legend-item">
              <View
                style={[styles.legendColor, { backgroundColor: "#0078A7" }]}
              />
              <Text style={styles.legendText}>
                N/A: {naCount} (
                {Math.round((naCount / totalCount) * 100) || 0}
                %)
              </Text>
            </View>
             <View style={styles.legendItem} testID="legend-item">
              {/* <View
                style={[styles.legendColor, { backgroundColor: "#95A5A6" }]}
              /> */}
              <Text style={styles.legendText1}>
                Total: {totalCount}
              </Text>
              {/* <Text style={styles.totalCountNumber}>
                {yesCount + noCount + naCount}
              </Text> */}
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    elevation: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    // Width will be controlled by parent container
  },
  header: {
    backgroundColor: "#034895",
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
  },
  headerIcon: {
    marginRight: 8,
  },
  headerText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  content: {
    padding: 20,
  },
  graphContainer: {
    alignItems: "center",
    marginVertical: 8,
    minHeight: 240,
  },
  chartWrapper: {
    position: "relative",
    width: 180,
    height: 180,
    backgroundColor: "transparent",
    borderRadius: 90,
    elevation: 0,
    shadowColor: "transparent",
  },
  totalCount: {
    position: "absolute",
    top: "50%",
    left: "57%",
    transform: [{ translateX: -30 }, { translateY: -22 }],
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    padding: 6,
    borderRadius: 6,
  },
  totalCountText: {
    fontSize: 12,
    color: "#666",
    fontWeight: "500",
  },
  totalCountNumber: {
    fontSize: 20,
    fontWeight: "600",
    color: "#333",
  },
  legendContainer: {
    flexDirection: "row",
    justifyContent: "center",
    flexWrap: "wrap",
    marginTop: 16,
    gap: 12,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    //backgroundColor: "#f8f9fa",
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 8,
  },
   legendText: {
     fontSize: 14,
     color: "#FFFFFF",
   },
   legendText1: {
     fontSize: 14,
     color: "#FFFFFF",
     fontWeight: "500",
   },
});

export default PreferredMethodChecklist;
